import { Fragment, useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Info } from 'lucide-react';
import type {
  CostRowHandlers,
  LineItemAddRequestHandler,
  LineItemCreateHandler,
  LineItemDeleteHandler,
  LineItemOpenHandler,
  LineItemPatchHandler,
  ProjectLayoutData,
  ProjectLayoutMode,
  ScopeFieldValueHandler,
  ShowCostHistoryHandler,
} from '../types';
import { capabilitiesForMode } from '../types';
import type { ElementInclusionHandler } from '../types';
import { variationFamilyMembers } from '../lib/variation-family';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { apiClient } from '@/lib/api-client';
import { AddSkuInline } from '@/features/estimate/components/AddSkuInline';
import { AddOtherInline } from '@/features/estimate/components/AddOtherInline';
import { ScopeStatementsTable } from './ScopeStatementsTable';
import { SectionHeading } from './SectionHeading';

/**
 * PRJ-2026-1089 — Footings (AUTO composed count) was seeded under
 * Element Details; SKUs linked to that field belonged with Foundation.
 * Remap for estimate grouping even when the API still returns the old
 * pinned groupLabel.
 */
export function effectiveScopeGroupLabel(
  scfCode: string | null | undefined,
  groupLabel: string | null | undefined,
): string | null {
  if (scfCode === 'SCF_PRECONFIGURED_DECK_FOOTING_COUNT') return 'Foundation';
  return groupLabel ?? null;
}

/** Synthetic nest target when Preconfigured Deck has no Structure cluster fields. */
export const STRUCTURE_NEST_FIELD_ID = '__structure_nest__';

/**
 * Groups that already own their SKUs — Structure remount must not steal
 * railing posts, roof framing, stair stringers, deck-board fasteners, etc.
 */
export function isProtectedScopeGroup(
  groupLabel: string | null | undefined,
): boolean {
  if (groupLabel == null) return false;
  const g = groupLabel.toLowerCase().trim();
  if (g === 'structure') return true;
  return (
    g.startsWith('deck board') ||
    g === 'decking' ||
    g === 'foundation' ||
    g.startsWith('rail') ||
    g.startsWith('fascia') ||
    g.startsWith('picture frame') ||
    g.startsWith('parting') ||
    g.startsWith('skirt') ||
    g.startsWith('roof') ||
    g.startsWith('stair') ||
    g.startsWith('landing')
  );
}

/** True when the current nest is a wrong home for framing (Dimensions / package). */
export function shouldRemountToStructure(
  groupLabel: string | null | undefined,
): boolean {
  if (groupLabel == null) return true;
  if (isProtectedScopeGroup(groupLabel)) return false;
  const g = groupLabel.toLowerCase().trim();
  return (
    g === 'element details' ||
    g.includes('dimension') ||
    g === 'custom deck' ||
    g === 'floor' ||
    g.startsWith('build') ||
    g === 'other'
  );
}

/**
 * Framing BOM (ledger, joists, beams, hangers, framing fasteners, support
 * posts). Purpose-first — does not pull railing "Structural posts", roof
 * framing, stair stringers, or deck-surface fasteners into Structure.
 */
export function isStructureScopeLineItem(
  purpose?: string | null,
  description?: string | null,
): boolean {
  const purposeLc = (purpose ?? '').toLowerCase().trim();
  const text = `${purpose ?? ''} ${description ?? ''}`.toLowerCase();

  // Other subcategories / non-framing assemblies
  if (
    /hidden fastener|cortex|plug|fusionloc|edgeloc|concealloc|face-screw for lumber/.test(
      text,
    )
  ) {
    return false;
  }
  if (
    /fascia|rail|skirt|lattice|footing|concrete bag|sono|helical|pier|deck board|picture.?frame|parting board/.test(
      text,
    )
  ) {
    return false;
  }
  if (
    /\broof\b|stringer|tread|riser|climate control|skylight|drink rail|\bgate\b/.test(
      text,
    )
  ) {
    return false;
  }
  if (/^stairs?\s*:|^landing\s*:/.test(purposeLc) || /stairs?\s*:|landing\s*:/.test(text)) {
    return false;
  }
  // Railing hardware uses "structural posts" / post-blocking without "rail"
  // in every purpose string — exclude explicitly.
  if (
    /structural posts?|structural post under sleeve|post blocking|post-to-|base-plate|hold-down|guard-post/.test(
      text,
    )
  ) {
    return false;
  }
  if (/azek porch|porch install guide fasteners|fasteners for porch/.test(text)) {
    return false;
  }
  if (
    /primary porch floor|primary deck surface|tongue-and-groove/.test(text)
  ) {
    return false;
  }
  if (/\bdecking\b/.test(text) && !/joist|beam|ledger|framing/.test(text)) {
    return false;
  }

  // Purpose allowlist (calc-engine / seed short purposes)
  if (
    /^(joists?|beams?|ledger|rim(?:\s+joists?)?|joist blocking|posts?|support posts?|structure)\b/.test(
      purposeLc,
    )
  ) {
    return true;
  }
  if (/^hardware\b/.test(purposeLc)) {
    return /hanger|connector|joist|beam|ledger|framing|structural/.test(text);
  }

  // Framing cues in purpose/description (CUSTOM_DECK / PORCH prose purposes)
  if (
    /ledger|joist|beam|girder|joist blocking|\brim\b|hanger|connector|floor framing|primary girder|support post/.test(
      text,
    )
  ) {
    return true;
  }
  // Framing fasteners only when paired with a framing cue (not bare "screws")
  if (
    /fasteners?|nails?|screws?/.test(text) &&
    /joist|beam|ledger|hanger|framing|structural/.test(text)
  ) {
    return true;
  }
  // Support posts / columns (not railing posts — already excluded)
  if (
    /\bposts?\b/.test(text) &&
    /support|framing|column|pt lumber/.test(text)
  ) {
    return true;
  }
  return false;
}

/**
 * EST-L1..L8 layout rebuild (2026-07-22, approved preview EST-L7) —
 * Section III "Project Scope".
 *
 * Structure keeps the Screen-6 mockup (one card per element, expand /
 * collapse, element totals, ADMINISTRATION block, subtotals). The card
 * BODY is rebuilt as the Kohrt-style two-column scope table:
 *
 *   - EST-L4 — shaded GROUP BARS from the fields' groupLabel; one
 *     `label | selection` row per visible, ANSWERED field. Hidden
 *     mirrors (contractVisibility=HIDDEN) and gated-off rows
 *     (visibilityResolved=false) never render; unanswered rows are
 *     omitted (Kohrt: one row per selection actually made).
 *   - EST-L5 — presentation-SCF rows ARE the consolidated selections
 *     ("Decking Selection | TimberTech, Terrain, Brown Oak"). Their
 *     member mirrors never render as rows; editing a consolidated row
 *     opens the member cascade (manufacturer → line → color) and on
 *     save PATCHes the members then recomputes + PATCHes the combined
 *     string FE-side with the mapping's separator.
 *   - EST-L3 — no CSF numbering/codes inline. Every row gets a ⓘ
 *     popover: source intake question, field code, provenance
 *     (customer-entered / engine-derived), members for consolidated
 *     rows. Estimator surfaces only.
 *   - EST-L1 — each selection row nests its assigned line items
 *     (PROD/SVC chip, description, qty × unit → line total, UNRESOLVED
 *     chip) followed by "+ Add material · + Add service". Line items
 *     linked to a MEMBER mirror nest under the consolidated row.
 *     Unlinked items fall to ADMINISTRATION (element-scoped labor /
 *     equipment / overhead).
 *   - Add material/service insert (Option C, 2026-07-28) — compact
 *     single row: Purpose (1–2 words) · Description catalog search
 *     (pick locks desc+MSKU; custom desc OK without MSKU) · Qty
 *     (blank unless engine/template) · Unit cost · Total. No bordered box.
 *
 * Customer surfaces (proposal / contract modes) render the clean
 * static equivalent from the STATEMENT ENGINE lines — the same
 * two-column table, consolidated rows only, no SKU nest, no ⓘ — via
 * <ScopeStatementsTable>. Cost visibility rules are unchanged:
 * showCostBreakdown gates every cost affordance exactly as before.
 */
export interface SectionIII_ProjectScopeProps {
  data: ProjectLayoutData;
  mode: ProjectLayoutMode;
  /** Legacy page-level toggle — when true, all elements start expanded. */
  showAll?: boolean;
  onShowAllToggle?: (next: boolean) => void;
  onLineItemPatch?: LineItemPatchHandler;
  onLineItemCreate?: LineItemCreateHandler;
  onLineItemDelete?: LineItemDeleteHandler;
  onLineItemOpen?: LineItemOpenHandler;
  onLineItemAddRequest?: LineItemAddRequestHandler;
  onCostRow?: CostRowHandlers;
  onFieldValue?: ScopeFieldValueHandler;
  onShowCostHistory?: ShowCostHistoryHandler;
  onElementInclusion?: ElementInclusionHandler;
}

/**
 * Option B (founder 2026-07-29) — parent line-item columns:
 * chip | Purpose | Description (flex) | Qty | Unit | Total | Delete
 * Qty/Unit are sized-to-fit; Description takes the leftover width.
 * Legacy inline path — renders only when no onLineItemOpen is supplied
 * (review mode keeps it).
 */
const LINE_ITEM_GRID =
  'flex flex-col gap-1 sm:grid sm:grid-cols-[auto_5.5rem_minmax(0,1fr)_2.5rem_2.5rem_5.5rem_auto] sm:items-baseline sm:gap-x-2';

/**
 * Option 4c (founder 2026-07-30) — the estimate-mode list row:
 * chip | Purpose | Qty | Unit | Total. Read-only; clicking opens the
 * page-level drawer. The chip gutter is FIXED (3.5rem) so rows share
 * column edges regardless of chip width, and Purpose — the 1–2 word
 * job the line does — is the identity column; the product description
 * lives on the drawer's bound card.
 */
const LINE_ITEM_LIST_GRID =
  'flex flex-wrap items-baseline gap-x-2 gap-y-0.5 sm:grid sm:grid-cols-[3.5rem_minmax(0,1fr)_3rem_2.75rem_5.5rem] sm:items-baseline sm:gap-x-2';

interface ScopeElement {
  id: string;
  elementTypeCode?: string | null;
  elementTypeDisplayName?: string | null;
  instanceLabel?: string | null;
  displayOrder?: number;
  marginPercentage?: string;
  clientIncluded?: boolean;
  isLocked?: boolean;
  isPendingAcceptance?: boolean;
  /** Bug 7 — last seeding failure for this element (warning, not a blocker). */
  seedingError?: string | null;
  variationLabel?: string | null;
  parentVariationOfId?: string | null;
  fields?: ScopeField[];
  statements?: ScopeStatement[];
  lineItems?: LineItem[];
  elementSubtotal?: string;
  elementOverhead?: string;
  elementTotal?: string;
}

interface ScopeStatement {
  scfCode?: string | null;
  groupLabel?: string | null;
  text: string;
  source?: string | null;
}

interface ScopeField {
  id: string;
  scfCode: string;
  scfLabel: string;
  groupLabel?: string | null;
  /** Linked intake question's input type (SINGLE_SELECT / BOOLEAN / NUMBER / …). */
  inputType?: string | null;
  /** Dropdown options for select-type linked questions ({ value, label }, sorted). */
  options?: Array<{ value: string; label: string }> | null;
  answerValue?: unknown;
  /** Server-computed display string (option labels, Yes/No, units, folded rows). */
  answerDisplay?: string | null;
  unit?: string | null;
  visibilityResolved?: boolean;
  displayOrder?: number;
  allowsLineItems?: boolean;
  /** Sprint 58 — HIDDEN mirrors never render as table rows. */
  contractVisibility?: string | null;
  /** EST-L3 — linked intake question code (chainFrom resolution + ⓘ). */
  questionCode?: string | null;
  /** EST-L3 — source intake question label for the ⓘ popover. */
  questionLabel?: string | null;
  /** EST-L3 — cheap provenance: CUSTOMER (intake answer) | DERIVED (engine). */
  provenance?: string | null;
  /** EST-L5 — cascade config for member levels (field/filters/chainFrom). */
  catalogFilterRules?: CascadeRules | null;
  /** EST-L5 — set on consolidated rows: ordered member field ids. */
  selectionMemberFieldIds?: string[] | null;
  /** EST-L5 — CONCAT separator convention (', '). */
  selectionSeparator?: string | null;
}

interface CascadeRules {
  field?: string;
  filters?: Record<string, unknown>;
  chainFrom?: Array<string | { code: string; param?: string }>;
  staticSource?: string;
}

interface LineItem {
  id: string;
  projectScopeElementFieldId?: string | null;
  type?: 'MATERIAL' | 'SERVICE';
  description: string;
  purpose?: string | null;
  quantity: string;
  unit: string;
  customUnit?: string | null;
  source?: string;
  isAllowance?: boolean;
  isLocked?: boolean;
  position?: number;
  /** Frozen machine seed at approve — null for estimator-added rows. */
  seedQuantity?: string | null;
  seedDescription?: string | null;
  seedPurpose?: string | null;
  changedFromSeed?: boolean;
  costs?: CostRow[];
  lineTotal?: string;
}

/** Keep nest order stable: API sorts by scope + purpose; position is fallback. */
function compareLineItems(a: LineItem, b: LineItem): number {
  return (a.position ?? 0) - (b.position ?? 0);
}

interface CostRow {
  id: string;
  productMsku?: string | null;
  serviceId?: string | null;
  description: string;
  unitCostSnapshot: string;
  unitCostOverride?: string | null;
  costSource: string;
  sourceLayerSnapshot?: {
    invoice?: number;
    partner?: number;
    default?: number;
    labor?: number;
  } | null;
}

export function SectionIII_ProjectScope({
  data,
  mode,
  showAll,
  onElementInclusion,
  onLineItemPatch,
  onLineItemCreate,
  onLineItemDelete,
  onLineItemOpen,
  onLineItemAddRequest,
  onCostRow,
  onFieldValue,
  onShowCostHistory,
}: SectionIII_ProjectScopeProps) {
  const caps = capabilitiesForMode(mode);
  const allElements = (data.scopeElements as ScopeElement[] | undefined) ?? [];
  const elements = allElements.filter((element) => {
    if (caps.showCostBreakdown) return true;
    return element.clientIncluded !== false;
  });
  const lineItemEditingEnabled = caps.isEditable && onLineItemPatch != null;
  const lineItemCreateEnabled = caps.isEditable && onLineItemCreate != null;
  const lineItemDeleteEnabled = caps.isEditable && onLineItemDelete != null;
  const costRowEditingEnabled = caps.isEditable && onCostRow != null;
  const fieldEditingEnabled = caps.isEditable && onFieldValue != null;
  const historyLinkEnabled = caps.showCostHistoryLink && onShowCostHistory != null;
  // Option 4c — drawer opening is a cost-surface affordance (viewing is
  // fine post-finalize); add-via-drawer requires edit mode.
  const drawerOpenEnabled = caps.showCostBreakdown && onLineItemOpen != null;
  const addRequestEnabled = caps.isEditable && onLineItemAddRequest != null;

  // Customer-facing surfaces (proposal / contract) keep today's
  // always-expanded scope rendering; estimator surfaces start
  // collapsed per the mockup unless the page passes showAll (review).
  const defaultExpanded = showAll === true || !caps.showCostBreakdown;

  return (
    <section
      id="section-3"
      className="scroll-mt-24"
      data-mode={mode}
      data-section="III"
    >
      <div className="rounded-xl border border-border bg-card shadow-sm">
        <SectionHeading numeral="III." title="Project Scope" className="px-6 pt-5 pb-1" />
        <p className="px-6 pb-4 font-public-sans text-xs text-muted-foreground">
          {caps.showCostBreakdown
            ? 'Each element lists its selections; materials and services nest under the selection they price.'
            : 'The selections and specifications for each element of this project.'}
        </p>
        {caps.variationSelectionInteractive && onElementInclusion ? (
          <VariationFamilyPickers
            elements={allElements}
            onPick={(id) => void onElementInclusion(id, true)}
          />
        ) : null}

        <div className="space-y-4 px-4 pb-6 sm:px-6">
          {elements.length === 0 ? (
            <div className="rounded-md border border-dashed border-border bg-muted/30 p-8 text-center font-public-sans text-sm text-muted-foreground">
              No scope elements yet.
            </div>
          ) : (
            elements.map((element, idx) => (
              <ElementBlock
                key={element.id}
                element={element}
                ordinal={idx + 1}
                defaultExpanded={defaultExpanded}
                caps={caps}
                onLineItemPatch={lineItemEditingEnabled ? onLineItemPatch : undefined}
                onLineItemCreate={lineItemCreateEnabled ? onLineItemCreate : undefined}
                onLineItemDelete={lineItemDeleteEnabled ? onLineItemDelete : undefined}
                onLineItemOpen={drawerOpenEnabled ? onLineItemOpen : undefined}
                onLineItemAddRequest={addRequestEnabled ? onLineItemAddRequest : undefined}
                onCostRow={costRowEditingEnabled ? onCostRow : undefined}
                onFieldValue={fieldEditingEnabled ? onFieldValue : undefined}
                onShowCostHistory={historyLinkEnabled ? onShowCostHistory : undefined}
              />
            ))
          )}
        </div>
      </div>
    </section>
  );
}

function ElementBlock({
  element,
  ordinal,
  defaultExpanded,
  caps,
  onLineItemPatch,
  onLineItemCreate,
  onLineItemDelete,
  onLineItemOpen,
  onLineItemAddRequest,
  onCostRow,
  onFieldValue,
  onShowCostHistory,
}: {
  element: ScopeElement;
  ordinal: number;
  defaultExpanded: boolean;
  caps: ReturnType<typeof capabilitiesForMode>;
  onLineItemPatch?: LineItemPatchHandler;
  onLineItemCreate?: LineItemCreateHandler;
  onLineItemDelete?: LineItemDeleteHandler;
  onLineItemOpen?: LineItemOpenHandler;
  onLineItemAddRequest?: LineItemAddRequestHandler;
  onCostRow?: CostRowHandlers;
  onFieldValue?: ScopeFieldValueHandler;
  onShowCostHistory?: ShowCostHistoryHandler;
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  // Locked elements (post-finalize) never expose edit affordances even
  // in estimate mode — snapshots are immutable. The drawer still OPENS
  // on locked rows (read-only view of the frozen costs).
  const locked = element.isLocked === true;
  const fieldValueHandler = locked ? undefined : onFieldValue;
  const lineItemCreateHandler = locked ? undefined : onLineItemCreate;
  const lineItemDeleteHandler = locked ? undefined : onLineItemDelete;
  const addRequestHandler = locked ? undefined : onLineItemAddRequest;
  const lineItemCount = (element.lineItems ?? []).length;

  return (
    <div
      className="overflow-hidden rounded-md border border-border bg-card"
      data-element-id={element.id}
      data-is-variation={element.parentVariationOfId ? 'true' : 'false'}
      data-client-included={element.clientIncluded ? 'true' : 'false'}
    >
      {/* Element header — collapsed shows name + line-item count + total */}
      <Button
        type="button"
        variant="ghost"
        onClick={() => setExpanded((e) => !e)}
        aria-expanded={expanded}
        className="flex h-auto w-full items-center justify-between gap-3 rounded-none px-4 py-3 text-left font-normal hover:bg-muted/30"
      >
        <span className="flex min-w-0 items-center gap-3">
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 font-public-sans text-xs font-bold text-primary">
            {ordinal}
          </span>
          <span className="min-w-0">
            {element.variationLabel ? (
              <span className="block font-public-sans text-[10px] font-bold uppercase tracking-[0.08em] text-primary">
                Variation: {element.variationLabel}
              </span>
            ) : null}
            <span className="block truncate font-newsreader text-base font-semibold text-foreground">
              {element.elementTypeDisplayName ?? element.elementTypeCode ?? '—'}
            </span>
            {element.instanceLabel && (
              <span className="block truncate font-public-sans text-xs text-muted-foreground">
                {element.instanceLabel}
              </span>
            )}
          </span>
          {element.seedingError ? (
            <span
              className="shrink-0 rounded-full bg-destructive/15 px-2 py-0.5 font-public-sans text-[10px] font-bold uppercase tracking-[0.06em] text-destructive"
              title={element.seedingError}
            >
              Seeding failed
            </span>
          ) : element.isPendingAcceptance ? (
            <span className="shrink-0 rounded-full bg-warning/15 px-2 py-0.5 font-public-sans text-[10px] font-bold uppercase tracking-[0.06em] text-warning">
              Not yet seeded
            </span>
          ) : null}
          {!element.clientIncluded && (
            <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 font-public-sans text-[10px] font-bold uppercase tracking-[0.06em] text-foreground-soft">
              Excluded
            </span>
          )}
        </span>
        <span className="flex shrink-0 items-center gap-3">
          {!expanded && caps.showCostBreakdown && (
            <span className="hidden font-public-sans text-xs text-muted-foreground sm:inline">
              {/* Bug 7 (2026-08-05): materials seed at submission; an
                  unseeded element here means the submit-time seed hasn't
                  run yet (it catches up on first open) or failed. */}
              {element.isPendingAcceptance && lineItemCount === 0
                ? 'materials seed on submission · click to expand'
                : `${lineItemCount} ${lineItemCount === 1 ? 'line item' : 'line items'} · click to expand`}
            </span>
          )}
          <span className="whitespace-nowrap font-public-sans text-base font-semibold text-primary">
            {formatMoney(element.elementTotal)}
          </span>
          {expanded ? (
            <ChevronDown className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          ) : (
            <ChevronRight className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          )}
        </span>
      </Button>

      {expanded && (
        <div className="border-t border-border px-3 pb-4 pt-4 sm:px-4">
          {caps.showCostBreakdown && element.seedingError && (
            // Bug 7 (founder): seeding failure is a warning, never a
            // blocker — the estimate exists, the estimator prices the
            // element manually and contacts LarkPM for a fix.
            <div
              className="mb-4 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 font-public-sans text-xs text-destructive"
              data-testid={`seeding-error-${element.id}`}
            >
              <span className="font-bold">Automatic material seeding failed for this element.</span>{' '}
              Line items were not generated — add them manually and contact
              LarkPM to request a fix.
              <span className="mt-1 block whitespace-pre-wrap font-mono text-[11px] opacity-80">
                {element.seedingError}
              </span>
            </div>
          )}
          {caps.showCostBreakdown ? (
            <EstimatorScopeBody
              element={element}
              caps={caps}
              onLineItemPatch={onLineItemPatch}
              onLineItemCreate={lineItemCreateHandler}
              onLineItemDelete={lineItemDeleteHandler}
              onLineItemOpen={onLineItemOpen}
              onLineItemAddRequest={addRequestHandler}
              onCostRow={onCostRow}
              onFieldValue={fieldValueHandler}
              onShowCostHistory={onShowCostHistory}
            />
          ) : (
            // EST-L7 — customer surfaces render the clean static table
            // from the statement engine (text unchanged, presentation
            // two-column): consolidated rows only, no SKUs, no ⓘ.
            // Pre-Wave-3 frozen snapshots without statements fall back
            // to label|value rows built from the visible fields.
            <ScopeStatementsTable
              statements={
                ((element.statements?.length ?? 0) > 0
                  ? element.statements!
                  : fallbackStatementsFromFields(element.fields ?? [])
                ).map((s) => ({
                  ...s,
                  groupLabel: effectiveScopeGroupLabel(s.scfCode, s.groupLabel),
                }))
              }
              fields={element.fields ?? []}
              testId={`customer-scope-table-${element.id}`}
            />
          )}
        </div>
      )}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────
 * Estimator surface — EST-L4 two-column table + EST-L1 SKU nest
 * ────────────────────────────────────────────────────────────────── */

function EstimatorScopeBody({
  element,
  caps,
  onLineItemPatch,
  onLineItemCreate,
  onLineItemDelete,
  onLineItemOpen,
  onLineItemAddRequest,
  onCostRow,
  onFieldValue,
  onShowCostHistory,
}: {
  element: ScopeElement;
  caps: ReturnType<typeof capabilitiesForMode>;
  onLineItemPatch?: LineItemPatchHandler;
  onLineItemCreate?: LineItemCreateHandler;
  onLineItemDelete?: LineItemDeleteHandler;
  onLineItemOpen?: LineItemOpenHandler;
  onLineItemAddRequest?: LineItemAddRequestHandler;
  onCostRow?: CostRowHandlers;
  onFieldValue?: ScopeFieldValueHandler;
  onShowCostHistory?: ShowCostHistoryHandler;
}) {
  const fields = element.fields ?? [];

  // EST-L5 — member mirrors fold into their consolidated row: they
  // never render as rows, and line items linked to them nest under the
  // consolidated row.
  //
  // Staging PRJ-2026-1089: calc-engine sometimes attaches Deck Boards to
  // SCF_DECK_BOARD_MATERIAL_TYPE (HIDDEN cascade root) which is NOT listed
  // in CONCAT selectionMemberFieldIds (only mfr/line/color are). Without
  // a same-group fallback those SKUs fell into Administration. Map every
  // HIDDEN field that shares a groupLabel with a consolidated selection
  // row onto that row.
  const ownerByMemberId = useMemo(() => {
    const m = new Map<string, string>();
    const selectionOwners = fields.filter(
      (f) => (f.selectionMemberFieldIds?.length ?? 0) > 0,
    );
    for (const f of selectionOwners) {
      for (const memberId of f.selectionMemberFieldIds ?? []) {
        if (memberId !== f.id) m.set(memberId, f.id);
      }
    }
    for (const f of fields) {
      if (f.contractVisibility !== 'HIDDEN') continue;
      if (m.has(f.id)) continue;
      if (!f.groupLabel) continue;
      const owner = selectionOwners.find(
        (o) =>
          o.id !== f.id &&
          o.groupLabel === f.groupLabel &&
          o.contractVisibility !== 'HIDDEN',
      );
      if (owner) m.set(f.id, owner.id);
    }
    return m;
  }, [fields]);

  const structureLineItems = useMemo(
    () =>
      (element.lineItems ?? []).filter((li) =>
        isStructureScopeLineItem(li.purpose, li.description),
      ),
    [element.lineItems],
  );

  // Rendered rows — §14.S5 visibility + Sprint 58 contract-visibility
  // + EST-L4 "no empty rows" (one row per selection actually made).
  // PRJ-2026-1089: Preconfigured Deck links ledger/joists/beams to the
  // package field (Element Details) and never mounts the Structure
  // cluster — inject a synthetic Structure nest so those SKUs sit under
  // a STRUCTURE group bar.
  const rowFields = useMemo(() => {
    const base = fields
      .filter(
        (f) =>
          f.visibilityResolved !== false &&
          f.contractVisibility !== 'HIDDEN' &&
          !ownerByMemberId.has(f.id) &&
          hasAnswer(f),
      )
      .slice()
      .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));

    const hasStructureGroup = base.some(
      (f) => effectiveScopeGroupLabel(f.scfCode, f.groupLabel) === 'Structure',
    );
    if (structureLineItems.length === 0 || hasStructureGroup) return base;

    const foundationOrders = base
      .filter(
        (f) =>
          effectiveScopeGroupLabel(f.scfCode, f.groupLabel) === 'Foundation',
      )
      .map((f) => f.displayOrder ?? 0);
    const deckOrders = base
      .filter((f) => {
        const g = (effectiveScopeGroupLabel(f.scfCode, f.groupLabel) ?? '')
          .toLowerCase();
        return g.startsWith('deck board') || g === 'decking';
      })
      .map((f) => f.displayOrder ?? 0);
    const afterFoundation =
      foundationOrders.length > 0 ? Math.max(...foundationOrders) : 0;
    const beforeDeck =
      deckOrders.length > 0 ? Math.min(...deckOrders) : afterFoundation + 1;
    const displayOrder =
      beforeDeck > afterFoundation
        ? (afterFoundation + beforeDeck) / 2
        : afterFoundation + 0.5;

    const synthetic: ScopeField = {
      id: STRUCTURE_NEST_FIELD_ID,
      scfCode: 'SCF_STRUCTURE_NEST',
      scfLabel: 'Structure',
      groupLabel: 'Structure',
      answerValue: 'Framing',
      answerDisplay: 'Framing',
      visibilityResolved: true,
      displayOrder,
      allowsLineItems: false,
      contractVisibility: 'VISIBLE',
      provenance: 'DERIVED',
    };
    return [...base, synthetic].sort(
      (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0),
    );
  }, [fields, ownerByMemberId, structureLineItems.length]);

  const fieldsById = useMemo(() => {
    const m = new Map<string, ScopeField>();
    for (const f of fields) m.set(f.id, f);
    for (const f of rowFields) m.set(f.id, f);
    return m;
  }, [fields, rowFields]);

  const structureAnchorId = useMemo(() => {
    if (structureLineItems.length === 0) return null;
    const preferred =
      rowFields.find((f) => f.scfCode === 'SCF_STRUCTURE_MATERIAL') ??
      rowFields.find(
        (f) =>
          effectiveScopeGroupLabel(f.scfCode, f.groupLabel) === 'Structure',
      );
    return preferred?.id ?? null;
  }, [rowFields, structureLineItems.length]);

  const renderedFieldIds = useMemo(
    () => new Set(rowFields.map((f) => f.id)),
    [rowFields],
  );

  // EST-L1 — line items nest under the row that prices them. Items
  // linked to a member mirror resolve to the consolidated row; items
  // whose target row isn't rendered fall back to ADMINISTRATION.
  // Structure framing SKUs remount under the Structure anchor even when
  // the calc-engine linked them to the package / Element Details row.
  const nestKeyForLineItem = (li: LineItem): string | 'admin' => {
    const resolvedFieldId = li.projectScopeElementFieldId
      ? (ownerByMemberId.get(li.projectScopeElementFieldId) ??
        li.projectScopeElementFieldId)
      : null;
    const currentField = resolvedFieldId
      ? fieldsById.get(resolvedFieldId)
      : undefined;
    const currentGroup = effectiveScopeGroupLabel(
      currentField?.scfCode,
      currentField?.groupLabel,
    );

    // Framing SKUs remount under Structure only when the persisted nest
    // is a wrong home (Element Details / Dimensions / Floor). Never steal
    // from Railing, Roof, Stairs, Deck Board, Fascia, Foundation, etc.
    if (
      structureAnchorId &&
      isStructureScopeLineItem(li.purpose, li.description) &&
      shouldRemountToStructure(currentGroup)
    ) {
      return structureAnchorId;
    }
    if (resolvedFieldId && renderedFieldIds.has(resolvedFieldId)) {
      return resolvedFieldId;
    }
    return 'admin';
  };

  const lineItemsByRow = useMemo(() => {
    const m = new Map<string | 'admin', LineItem[]>();
    for (const li of element.lineItems ?? []) {
      const key = nestKeyForLineItem(li);
      const bucket = m.get(key) ?? [];
      bucket.push(li);
      m.set(key, bucket);
    }
    // Preserve scope-spec / purpose order from the resolve payload.
    for (const [key, bucket] of m) {
      m.set(key, [...bucket].sort(compareLineItems));
    }
    return m;
  }, [
    element.lineItems,
    ownerByMemberId,
    renderedFieldIds,
    structureAnchorId,
    fieldsById,
  ]);

  const adminItems = lineItemsByRow.get('admin') ?? [];
  const lineItemCount = (element.lineItems ?? []).length;

  // Footer math — selection-attached vs administration. Together they
  // equal elementTotal. Rendered only once the element actually has
  // line items (a pre-estimate review shows no empty $ rows).
  // Use nestKeyForLineItem so remapped framing SKUs stay in Selection.
  const attachedSubtotal = sumLineTotals(
    (element.lineItems ?? []).filter((li) => nestKeyForLineItem(li) !== 'admin'),
  );
  const adminSubtotal =
    sumLineTotals(adminItems) + toNumber(element.elementOverhead);

  // Polish round 2026-07-23 #1 — group bars are BUCKETED by the field's
  // own groupLabel, not by adjacency runs: every row renders under its
  // own group's bar even when displayOrder interleaves groups (the
  // skirting presentation row anchored at its driving mirror's order
  // previously orphaned above the first bar / leaked under RAILING —
  // GATE). Bucket order = first appearance in displayOrder; rows with
  // a null groupLabel collect into a trailing OTHER bar (stated
  // choice) so an orphan is structurally impossible.
  const groups: Array<{ label: string | null; fields: ScopeField[] }> = [];
  {
    const indexByLabel = new Map<string, number>();
    let otherIndex = -1;
    for (const f of rowFields) {
      const label = effectiveScopeGroupLabel(f.scfCode, f.groupLabel);
      if (label == null) {
        if (otherIndex < 0) {
          otherIndex = groups.length;
          groups.push({ label: null, fields: [] });
        }
        groups[otherIndex].fields.push(f);
        continue;
      }
      let i = indexByLabel.get(label);
      if (i == null) {
        i = groups.length;
        indexByLabel.set(label, i);
        groups.push({ label, fields: [] });
      }
      groups[i].fields.push(f);
    }
    // OTHER always trails the labeled groups.
    if (otherIndex >= 0 && otherIndex !== groups.length - 1) {
      const [other] = groups.splice(otherIndex, 1);
      groups.push(other);
    }
  }

  // Polish round #5 — ONE add strip per group section (was one per
  // row). Target-field resolution rule: the group's consolidated
  // selection row when present (first field carrying
  // selectionMemberFieldIds), else the group's first field that allows
  // line items, else no strip for that group. ADMINISTRATION keeps its
  // own strip.
  const addTargetFor = (group: { fields: ScopeField[] }): ScopeField | null =>
    group.fields.find((f) => (f.selectionMemberFieldIds?.length ?? 0) > 0) ??
    group.fields.find((f) => f.allowsLineItems !== false) ??
    null;

  return (
    <>
      <div className="overflow-x-auto">
        <table
          className="w-full border-collapse font-public-sans text-sm"
          data-testid="estimator-scope-table"
        >
          <tbody>
            {groups.map((group, gi) => {
              const addTarget =
                onLineItemCreate != null ? addTargetFor(group) : null;
              return (
                <Fragment key={`group-${group.label ?? 'other'}-${gi}`}>
                  <tr className="block sm:table-row">
                    <td
                      colSpan={2}
                      className="block w-full bg-primary px-3 py-1.5 font-public-sans text-xs font-bold uppercase tracking-[0.06em] text-primary-foreground sm:table-cell"
                    >
                      {(group.label ?? 'Other').toUpperCase()}
                    </td>
                  </tr>
                  {group.fields.map((f) => (
                    <FieldRow
                      key={f.id}
                      field={f}
                      fieldsById={fieldsById}
                      lineItems={lineItemsByRow.get(f.id) ?? []}
                      caps={caps}
                      onLineItemPatch={onLineItemPatch}
                      onLineItemDelete={onLineItemDelete}
                      onLineItemOpen={onLineItemOpen}
                      onCostRow={onCostRow}
                      onFieldValue={onFieldValue}
                      onShowCostHistory={onShowCostHistory}
                    />
                  ))}
                  {addTarget != null && (
                    <tr data-testid={`group-add-${addTarget.id}`} className="block sm:table-row">
                      <td
                        colSpan={2}
                        className="block w-full border-b border-border/60 px-3 py-0.5 sm:table-cell sm:pl-8"
                      >
                        <AddSkuControls
                          elementId={element.id}
                          fieldId={addTarget.id}
                          onLineItemCreate={onLineItemCreate!}
                          onAddRequest={onLineItemAddRequest}
                        />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}

            {/* ADMINISTRATION — element-scoped items (labor / equipment /
                overhead) + estimator-added misc SKUs. */}
            {(adminItems.length > 0 ||
              toNumber(element.elementOverhead) > 0 ||
              onLineItemCreate != null) && (
              <>
                <tr className="block sm:table-row">
                  <td
                    colSpan={2}
                    className="block w-full bg-primary px-3 py-1.5 font-public-sans text-xs font-bold uppercase tracking-[0.06em] text-primary-foreground sm:table-cell"
                    data-testid="administration-bar"
                  >
                    Administration
                    <span className="ml-2 font-semibold normal-case tracking-normal text-primary-foreground/80">
                      — element-scoped SKUs + overhead
                    </span>
                  </td>
                </tr>
                <tr data-testid="administration-block" className="block sm:table-row">
                  <td colSpan={2} className="block w-full px-3 py-1 sm:table-cell">
                    {adminItems.map((li) => (
                      <LineItemRow
                        key={li.id}
                        lineItem={li}
                        caps={caps}
                        onLineItemPatch={onLineItemPatch}
                        onLineItemDelete={onLineItemDelete}
                        onOpen={onLineItemOpen}
                        onCostRow={onCostRow}
                        onShowCostHistory={onShowCostHistory}
                      />
                    ))}
                    {toNumber(element.elementOverhead) > 0 && (
                      <div
                        className="flex flex-wrap items-baseline justify-between gap-2 py-2"
                        data-testid="overhead-row"
                      >
                        <span className="flex items-baseline gap-2">
                          <span className="rounded bg-muted px-1.5 py-0.5 font-public-sans text-[10px] font-bold uppercase tracking-[0.06em] text-foreground-soft">
                            OH
                          </span>
                          <span className="font-public-sans text-sm font-medium text-foreground">
                            Overhead allocation
                          </span>
                          {toNumber(element.marginPercentage) > 0 && (
                            <span className="font-public-sans text-xs text-muted-foreground">
                              {trimPct(toNumber(element.marginPercentage))}% of selections
                            </span>
                          )}
                        </span>
                        <span className="whitespace-nowrap font-mono text-sm text-foreground-soft">
                          {formatMoney(element.elementOverhead)}
                        </span>
                      </div>
                    )}
                    {adminItems.length === 0 &&
                      toNumber(element.elementOverhead) <= 0 && (
                        <p className="py-1.5 font-public-sans text-xs italic text-muted-foreground">
                          No administration line items yet.
                        </p>
                      )}
                    {onLineItemCreate != null && (
                      <AddSkuControls
                        elementId={element.id}
                        fieldId={null}
                        allowOther
                        onLineItemCreate={onLineItemCreate}
                        onAddRequest={onLineItemAddRequest}
                      />
                    )}
                  </td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>

      {rowFields.length === 0 && (
        <p className="py-3 font-public-sans text-sm italic text-muted-foreground">
          No answered scope selections on this element yet.
        </p>
      )}

      {/* Footer totals — only meaningful once line items exist. */}
      {lineItemCount > 0 && (
        <div className="mt-4 rounded-md bg-muted/30 px-4 py-3" data-testid="element-footer">
          <TotalRow label="Selection SKUs subtotal" value={formatMoney(String(attachedSubtotal))} />
          <TotalRow label="Administration subtotal" value={formatMoney(String(adminSubtotal))} />
          <div className="mt-1 border-t border-border pt-1">
            <TotalRow label="Element total" value={formatMoney(element.elementTotal)} bold />
          </div>
        </div>
      )}
    </>
  );
}

/** EST-L4 — one `label | selection` row plus its EST-L1 SKU nest.
 * Add-SKU controls live on the GROUP strip (polish #5), not per row. */
function FieldRow({
  field,
  fieldsById,
  lineItems,
  caps,
  onLineItemPatch,
  onLineItemDelete,
  onLineItemOpen,
  onCostRow,
  onFieldValue,
  onShowCostHistory,
}: {
  field: ScopeField;
  fieldsById: Map<string, ScopeField>;
  lineItems: LineItem[];
  caps: ReturnType<typeof capabilitiesForMode>;
  onLineItemPatch?: LineItemPatchHandler;
  onLineItemDelete?: LineItemDeleteHandler;
  onLineItemOpen?: LineItemOpenHandler;
  onCostRow?: CostRowHandlers;
  onFieldValue?: ScopeFieldValueHandler;
  onShowCostHistory?: ShowCostHistoryHandler;
}) {
  const isConsolidated = (field.selectionMemberFieldIds?.length ?? 0) > 0;
  const [cascadeOpen, setCascadeOpen] = useState(false);
  const hasNest = lineItems.length > 0 || cascadeOpen;

  return (
    <>
      {/* Micro round 2026-07-23 #2 — at <sm each row stacks (label line,
          value line beneath, full width) so the table FITS 390px instead
          of clipping in the h-scroll container; sm+ keeps the two-column
          table-cell layout. */}
      {/* Founder 2026-07-29 — flattened box structure: horizontal row
          separators only (no per-cell border boxes); nests indent
          under their row instead of rendering as gray boxes-in-boxes. */}
      <tr data-field-code={field.scfCode} className="block sm:table-row">
        <td className="block w-full bg-muted/30 px-3 py-1.5 align-top font-semibold text-foreground sm:table-cell sm:w-1/3 sm:min-w-32 sm:border-b sm:border-border/60 sm:py-2">
          {field.scfLabel}
        </td>
        <td className="block w-full border-b border-border/60 px-3 py-1.5 align-top sm:table-cell">
          <div className="flex items-start justify-between gap-2">
            <span className="min-w-0 font-public-sans text-sm text-foreground">
              {isConsolidated ? (
                <span className="inline-flex flex-wrap items-baseline gap-2">
                  <span>{displayAnswer(field)}</span>
                  {onFieldValue != null && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-auto px-1 py-0 text-xs font-semibold text-primary hover:text-primary-dim"
                      aria-label={`Edit ${field.scfLabel}`}
                      onClick={() => setCascadeOpen((o) => !o)}
                    >
                      {cascadeOpen ? 'Close' : 'Edit'}
                    </Button>
                  )}
                </span>
              ) : (
                <CsfFieldValue field={field} onFieldValue={onFieldValue} />
              )}
            </span>
            <InfoPopover field={field} fieldsById={fieldsById} />
          </div>
        </td>
      </tr>

      {hasNest && (
        <tr data-testid={`sku-nest-${field.id}`} className="block sm:table-row">
          <td colSpan={2} className="block w-full border-b border-border/60 py-1 pl-6 pr-3 sm:table-cell sm:pl-10">
            {cascadeOpen && onFieldValue != null && (
              <SelectionCascadeEditor
                field={field}
                fieldsById={fieldsById}
                onFieldValue={onFieldValue}
                onDone={() => setCascadeOpen(false)}
              />
            )}
            {lineItems.map((li) => (
              <LineItemRow
                key={li.id}
                lineItem={li}
                caps={caps}
                onLineItemPatch={onLineItemPatch}
                onLineItemDelete={onLineItemDelete}
                onOpen={onLineItemOpen}
                onCostRow={onCostRow}
                onShowCostHistory={onShowCostHistory}
              />
            ))}
          </td>
        </tr>
      )}
    </>
  );
}

/* ────────────────────────────────────────────────────────────────────
 * EST-L3 — ⓘ provenance popover
 * ────────────────────────────────────────────────────────────────── */

function provenanceLabel(p: string | null | undefined): string {
  if (p === 'CUSTOMER') return 'Customer-entered';
  if (p === 'DERIVED') return 'Engine-derived';
  return '—';
}

function InfoPopover({
  field,
  fieldsById,
}: {
  field: ScopeField;
  fieldsById: Map<string, ScopeField>;
}) {
  const members = (field.selectionMemberFieldIds ?? [])
    .filter((id) => id !== field.id)
    .map((id) => fieldsById.get(id))
    .filter((f): f is ScopeField => f != null);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          aria-label={`About ${field.scfLabel}`}
          className="h-5 w-5 shrink-0 rounded-full p-0 text-muted-foreground hover:text-primary"
        >
          <Info className="h-3.5 w-3.5" aria-hidden="true" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-72 p-3 font-public-sans text-xs"
        data-testid={`info-popover-${field.id}`}
      >
        <p className="mb-2 font-bold uppercase tracking-[0.06em] text-muted-foreground">
          Field detail
        </p>
        <dl className="space-y-1.5">
          <div>
            <dt className="font-semibold text-foreground-soft">Source intake question</dt>
            <dd className="text-foreground">{field.questionLabel ?? '—'}</dd>
          </div>
          <div>
            <dt className="font-semibold text-foreground-soft">Field code</dt>
            {/* Polish #4 — long codes wrap instead of overflowing. */}
            <dd className="break-all font-mono text-[11px] text-foreground">{field.scfCode}</dd>
          </div>
          <div>
            <dt className="font-semibold text-foreground-soft">Provenance</dt>
            <dd className="text-foreground">{provenanceLabel(field.provenance)}</dd>
          </div>
          {members.length > 0 && (
            <div>
              <dt className="font-semibold text-foreground-soft">Selection members</dt>
              <dd className="text-foreground">
                <ul className="mt-0.5 space-y-0.5">
                  {members.map((m) => (
                    <li key={m.id}>
                      <span className="text-foreground-soft">{m.scfLabel}:</span>{' '}
                      {displayAnswer(m)}
                    </li>
                  ))}
                </ul>
              </dd>
            </div>
          )}
        </dl>
      </PopoverContent>
    </Popover>
  );
}

/* ────────────────────────────────────────────────────────────────────
 * EST-L5 — consolidated-selection member cascade editor
 * ────────────────────────────────────────────────────────────────── */

/**
 * Opens under a consolidated row. One level per member field
 * (manufacturer → line → color), options loaded from
 * /calculation/master-product-options with the same exact-match +
 * chainFrom semantics as the intake CatalogCascadeWidget; free entry
 * when the catalog returns nothing. On save it PATCHes each member
 * mirror, then recomputes the combined string FE-side (members joined
 * with the mapping's separator, empties skipped — the CONCAT_FIELDS
 * convention) and PATCHes it onto the consolidated row. This also
 * closes the known follow-up where member edits left the Selection
 * string stale.
 */
function SelectionCascadeEditor({
  field,
  fieldsById,
  onFieldValue,
  onDone,
}: {
  field: ScopeField;
  fieldsById: Map<string, ScopeField>;
  onFieldValue: ScopeFieldValueHandler;
  onDone: () => void;
}) {
  const separator = field.selectionSeparator ?? ', ';
  const members = useMemo(
    () =>
      (field.selectionMemberFieldIds ?? [])
        .map((id) => fieldsById.get(id))
        .filter((f): f is ScopeField => f != null),
    [field.selectionMemberFieldIds, fieldsById],
  );

  const initialPicks = useMemo(() => {
    const combinedSegments = String(
      typeof field.answerValue === 'string' ? field.answerValue : '',
    )
      .split(separator)
      .map((s) => s.trim());
    return members.map((m, i) => {
      if (m.id === field.id) {
        // Self-target concat root (railing): the mirror stores the
        // combined string; the level's own value is its first segment.
        return combinedSegments[i] ?? '';
      }
      return typeof m.answerValue === 'string' ? m.answerValue : '';
    });
  }, [members, field.id, field.answerValue, separator]);

  const [picks, setPicks] = useState<string[]>(initialPicks);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const combined = picks
    .map((p) => p.trim())
    .filter((p) => p !== '')
    .join(separator);

  const save = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      for (let i = 0; i < members.length; i++) {
        const m = members[i];
        if (m.id === field.id) continue; // combined patched last
        const next = picks[i].trim();
        const current = typeof m.answerValue === 'string' ? m.answerValue : '';
        if (next !== current) {
          await onFieldValue({ fieldId: m.id, value: next });
        }
      }
      await onFieldValue({ fieldId: field.id, value: combined });
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="my-2 space-y-2 rounded border border-outline-variant bg-surface-low p-3"
      data-testid={`cascade-editor-${field.id}`}
    >
      <p className="font-public-sans text-[10px] font-bold uppercase tracking-[0.08em] text-muted-foreground">
        Edit {field.scfLabel}
      </p>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        {members.map((m, i) => (
          <MemberLevel
            key={m.id}
            member={m}
            levelIndex={i}
            members={members}
            picks={picks}
            fieldsById={fieldsById}
            disabled={busy}
            onPick={(v) =>
              setPicks((prev) => {
                const next = [...prev];
                next[i] = v;
                // A change upstream invalidates downstream picks.
                for (let j = i + 1; j < next.length; j++) next[j] = '';
                return next;
              })
            }
          />
        ))}
      </div>
      <p className="font-public-sans text-xs text-muted-foreground">
        Saves as: <span className="font-medium text-foreground">{combined || '—'}</span>
      </p>
      {error && <p className="text-xs text-destructive">{error}</p>}
      <div className="flex items-center justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" disabled={busy} onClick={onDone}
          className="h-7 px-2 text-xs text-foreground-soft hover:bg-surface hover:text-foreground">
          Cancel
        </Button>
        <Button type="button" size="sm" disabled={busy || combined === ''} onClick={() => void save()}
          className="h-7 px-2 text-xs font-semibold">
          {busy ? 'Saving…' : 'Save selection'}
        </Button>
      </div>
    </div>
  );
}

/** One cascade level — Select from catalog options, free text fallback. */
function MemberLevel({
  member,
  levelIndex,
  members,
  picks,
  fieldsById,
  disabled,
  onPick,
}: {
  member: ScopeField;
  levelIndex: number;
  members: ScopeField[];
  picks: string[];
  fieldsById: Map<string, ScopeField>;
  disabled: boolean;
  onPick: (v: string) => void;
}) {
  const rules = member.catalogFilterRules ?? null;
  const pick = picks[levelIndex] ?? '';

  // Resolve chainFrom → query params. Upstream values come from the
  // cascade's own picks first, then from sibling field answers (same
  // semantics as CatalogCascadeWidget, minus intake-only visibility).
  const query = useMemo(() => {
    if (!rules?.field) return null;
    const params = new URLSearchParams();
    params.set('field', rules.field);
    params.set('matchMode', 'exact');
    for (const [k, v] of Object.entries(rules.filters ?? {})) {
      if (v !== undefined && v !== null && v !== '') params.set(k, String(v));
    }
    for (const entry of rules.chainFrom ?? []) {
      const code = typeof entry === 'string' ? entry : entry.code;
      const upstreamIdx = members.findIndex(
        (m, i) => i < levelIndex && m.questionCode === code,
      );
      let value: string | null = null;
      if (upstreamIdx >= 0) {
        value = picks[upstreamIdx]?.trim() || null;
      } else {
        for (const f of fieldsById.values()) {
          if (f.questionCode === code && typeof f.answerValue === 'string' && f.answerValue !== '') {
            value = f.answerValue;
            break;
          }
        }
      }
      let param = typeof entry === 'string' ? undefined : entry.param;
      if (!param) {
        const upstreamField =
          upstreamIdx >= 0
            ? members[upstreamIdx]
            : [...fieldsById.values()].find((f) => f.questionCode === code);
        param = upstreamField?.catalogFilterRules?.field;
      }
      if (!param || !value) continue; // partial chain — query the rest
      params.set(param, value);
    }
    return params.toString();
  }, [rules, members, picks, levelIndex, fieldsById]);

  const [options, setOptions] = useState<string[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!query) {
      setOptions([]);
      setLoaded(true);
      return;
    }
    let cancelled = false;
    setLoaded(false);
    apiClient
      .get<{ values: string[] }>(`/calculation/master-product-options?${query}`)
      .then((res) => {
        if (cancelled) return;
        setOptions(Array.isArray(res?.values) ? res.values : []);
      })
      .catch(() => {
        if (!cancelled) setOptions([]);
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [query]);

  const label = member.scfLabel;
  const freeText = loaded && options.length === 0;

  return (
    <div>
      <span className="block font-public-sans text-xs text-foreground-soft">{label}</span>
      {freeText || !rules?.field ? (
        <Input
          value={pick}
          disabled={disabled}
          aria-label={label}
          placeholder={`Type ${label.toLowerCase()}`}
          className="mt-1 h-8 text-sm"
          onChange={(e) => onPick(e.target.value)}
        />
      ) : (
        <Select value={pick} disabled={disabled || !loaded} onValueChange={onPick}>
          <SelectTrigger className="mt-1 h-8 text-sm" aria-label={label}>
            <SelectValue placeholder={loaded ? 'Select…' : 'Loading…'}>
              {pick || undefined}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {/* Keep a non-catalog current value selectable. */}
            {pick && !options.includes(pick) && (
              <SelectItem value={pick}>{pick}</SelectItem>
            )}
            {options.map((o) => (
              <SelectItem key={o} value={o}>
                {o}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────
 * Field value editing (non-consolidated rows) — unchanged machinery
 * ────────────────────────────────────────────────────────────────── */

function CsfFieldValue({
  field,
  onFieldValue,
}: {
  field: ScopeField;
  onFieldValue?: ScopeFieldValueHandler;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isSelect =
    field.inputType === 'SINGLE_SELECT' && (field.options?.length ?? 0) > 0;
  const isBoolean =
    field.inputType === 'BOOLEAN' || typeof field.answerValue === 'boolean';
  const isNumber =
    field.inputType === 'NUMBER' || typeof field.answerValue === 'number';
  const isMultiSelect = field.inputType === 'MULTI_SELECT';

  if (!onFieldValue || isMultiSelect) {
    return <>{displayAnswer(field)}</>;
  }

  const commit = async (value: unknown) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await onFieldValue({ fieldId: field.id, value });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  let control: React.ReactNode;
  if (isSelect) {
    const current =
      typeof field.answerValue === 'string' ? field.answerValue : '';
    const currentLabel =
      field.options!.find((o) => o.value === current)?.label ??
      displayAnswer(field);
    control = (
      <Select
        value={current}
        disabled={busy}
        onValueChange={(v) => {
          if (v !== current) void commit(v);
        }}
      >
        <SelectTrigger
          className="h-7 w-auto min-w-[8rem] border-0 bg-transparent px-1 text-sm hover:bg-surface-low focus:ring-0"
          aria-label={`Edit ${field.scfLabel}`}
        >
          <SelectValue>{currentLabel}</SelectValue>
        </SelectTrigger>
        <SelectContent>
          {field.options!.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  } else if (isBoolean) {
    const current =
      field.answerValue === true
        ? 'true'
        : field.answerValue === false
          ? 'false'
          : '';
    control = (
      <Select
        value={current}
        disabled={busy}
        onValueChange={(v) => {
          if (v !== current) void commit(v === 'true');
        }}
      >
        <SelectTrigger
          className="h-7 w-auto min-w-[5rem] border-0 bg-transparent px-1 text-sm hover:bg-surface-low focus:ring-0"
          aria-label={`Edit ${field.scfLabel}`}
        >
          <SelectValue>{displayAnswer(field)}</SelectValue>
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="true">Yes</SelectItem>
          <SelectItem value="false">No</SelectItem>
        </SelectContent>
      </Select>
    );
  } else if (isNumber) {
    control = (
      <InlineTextField
        value={
          field.answerValue == null ? '' : String(field.answerValue as number)
        }
        ariaLabel={field.scfLabel}
        className="text-right"
        onCommit={async (next) => {
          const trimmed = next.trim();
          if (trimmed === String(field.answerValue ?? '')) return;
          const n = Number(trimmed);
          if (trimmed === '' || !Number.isFinite(n)) {
            setError('Not a valid number');
            return;
          }
          await commit(n);
        }}
      />
    );
  } else {
    control = (
      <InlineTextField
        value={
          typeof field.answerValue === 'string'
            ? field.answerValue
            : field.answerValue == null
              ? ''
              : displayAnswer(field)
        }
        ariaLabel={field.scfLabel}
        onCommit={async (next) => {
          if (next === field.answerValue) return;
          await commit(next);
        }}
      />
    );
  }

  return (
    <span className="inline-flex flex-col items-start gap-0.5">
      {control}
      {error && <span className="text-xs text-destructive">{error}</span>}
    </span>
  );
}

/* ────────────────────────────────────────────────────────────────────
 * EST-L1 SKU nest rows + EST-L8 add-SKU row
 * ────────────────────────────────────────────────────────────────── */

/**
 * Option B (founder 2026-07-29) — parent line item:
 * PROD/SVC | Purpose | Description | Qty | Unit | Total | Delete
 * Nested cost rows show unit cost / source only (no cramped ×).
 * Delete removes the whole line item (and cascaded costs).
 */
function LineItemRow({
  lineItem,
  caps,
  onLineItemPatch,
  onLineItemDelete,
  onOpen,
  onCostRow,
  onShowCostHistory,
}: {
  lineItem: LineItem;
  caps: ReturnType<typeof capabilitiesForMode>;
  onLineItemPatch?: LineItemPatchHandler;
  onLineItemDelete?: LineItemDeleteHandler;
  /**
   * Option 4c (founder 2026-07-30) — when supplied, the row renders as
   * the read-only purpose-first list row and opens the page drawer;
   * all inline editing and the nested cost rows move into the drawer.
   */
  onOpen?: LineItemOpenHandler;
  onCostRow?: CostRowHandlers;
  onShowCostHistory?: ShowCostHistoryHandler;
}) {
  const editable = onLineItemPatch != null && lineItem.isLocked !== true;
  const deletable = onLineItemDelete != null && lineItem.isLocked !== true;
  const costEditable = onCostRow != null && lineItem.isLocked !== true;
  const [addingCost, setAddingCost] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const skuCode = skuCodeFor(lineItem);
  const unresolved = (lineItem.costs ?? []).some(
    (c) => c.costSource === 'UNRESOLVED',
  );

  // Polish round 2026-07-23 #3 — UNRESOLVED rows keep the SAME columns
  // as normal rows. A derivation row shaped
  // "UNRESOLVED — <what>: <reason>" renders the <what> as the title
  // and the reason as a second muted line.
  const rawDesc = lineItem.description ?? '';
  const unresolvedMatch = unresolved
    ? rawDesc.match(/^UNRESOLVED\s*[—-]\s*([^:]+):\s*([\s\S]+)$/)
    : null;
  const displayTitle = unresolvedMatch
    ? capitalizeFirst(unresolvedMatch[1].trim())
    : rawDesc;
  const unresolvedReason = unresolvedMatch ? unresolvedMatch[2].trim() : null;
  const purpose = (lineItem.purpose ?? '').trim();

  const handleDelete = async () => {
    if (!deletable || deleting) return;
    const label = purpose ? `${purpose} — ${rawDesc}` : rawDesc;
    if (!confirm(`Delete line item "${label}"?`)) return;
    setDeleting(true);
    try {
      await onLineItemDelete!(lineItem.id);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to delete line item.';
      window.alert(message);
    } finally {
      setDeleting(false);
    }
  };

  // Option 4c — read-only purpose-first list row; the drawer owns all
  // editing and the cost breakdown. Purpose is the identity column
  // (description fallback for legacy rows without one).
  if (onOpen != null) {
    const listLabel = purpose || displayTitle;
    return (
      <div
        role="button"
        tabIndex={0}
        data-line-item-id={lineItem.id}
        data-testid="line-item-row"
        aria-label={`Open line item ${listLabel}`}
        className={`${LINE_ITEM_LIST_GRID} cursor-pointer border-b border-border py-1.5 text-sm last:border-b-0 hover:bg-muted/50 focus-visible:bg-muted/50 focus-visible:outline-none`}
        onClick={() => onOpen(lineItem.id)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onOpen(lineItem.id);
          }
        }}
      >
        <span className="flex shrink-0 items-baseline">
          {unresolved ? (
            <span className="shrink-0 rounded bg-destructive/15 px-1.5 py-0.5 font-public-sans text-[10px] font-bold uppercase tracking-[0.06em] text-destructive">
              Unres
            </span>
          ) : (
            <TypeChip type={lineItem.type} />
          )}
        </span>
        <span className="flex min-w-0 items-baseline gap-x-2">
          <span
            className="min-w-0 truncate font-medium text-foreground"
            data-col="purpose"
            title={listLabel}
          >
            {listLabel}
          </span>
          {lineItem.changedFromSeed && (
            <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 font-public-sans text-[10px] font-bold uppercase tracking-[0.06em] text-muted-foreground">
              Edited
            </span>
          )}
          {lineItem.isAllowance && (
            <span className="shrink-0 rounded bg-warning/15 px-1.5 py-0.5 font-public-sans text-[10px] font-bold uppercase tracking-[0.06em] text-warning">
              Allowance
            </span>
          )}
        </span>
        <span
          className="whitespace-nowrap font-mono text-foreground-soft sm:justify-self-end sm:text-right"
          data-col="quantity"
        >
          {lineItem.quantity}
        </span>
        <span
          className="truncate text-xs text-muted-foreground"
          data-col="unit"
          title={lineItem.customUnit ?? lineItem.unit}
        >
          {lineItem.customUnit ?? lineItem.unit}
        </span>
        <span
          className="whitespace-nowrap font-mono text-sm font-semibold text-foreground sm:justify-self-end sm:text-right"
          data-col="line-total"
        >
          {formatMoney(lineItem.lineTotal)}
        </span>
      </div>
    );
  }

  return (
    <div
      className="border-b border-border py-2 text-sm last:border-b-0"
      data-line-item-id={lineItem.id}
      data-testid="line-item-row"
    >
      <div className={LINE_ITEM_GRID}>
        <div className="flex shrink-0 items-baseline gap-1">
          <TypeChip type={lineItem.type} />
          {unresolved && (
            <span className="shrink-0 rounded bg-destructive/15 px-1.5 py-0.5 font-public-sans text-[10px] font-bold uppercase tracking-[0.06em] text-destructive">
              Unresolved
            </span>
          )}
        </div>

        <span
          className="min-w-0 truncate text-xs text-muted-foreground"
          data-col="purpose"
          title={purpose || undefined}
        >
          {purpose || '—'}
        </span>

        <div className="flex min-w-0 items-baseline gap-x-2 gap-y-1">
          {skuCode && (
            <span className="shrink-0 font-mono text-[11px] text-muted-foreground">
              {skuCode}
            </span>
          )}
          {editable && unresolvedMatch == null ? (
            <InlineTextField
              value={rawDesc}
              ariaLabel="Line item description"
              className="min-w-0 flex-1 font-medium text-foreground"
              truncateDisplay
              onCommit={async (next) => {
                if (next === lineItem.description) return;
                await onLineItemPatch!(lineItem.id, { description: next });
              }}
            />
          ) : (
            <span
              className="min-w-0 flex-1 whitespace-normal font-medium text-foreground line-clamp-1 sm:line-clamp-2"
              title={rawDesc}
            >
              {displayTitle}
            </span>
          )}
          {lineItem.changedFromSeed && (
            <span
              className="shrink-0 rounded bg-muted px-1.5 py-0.5 font-public-sans text-[10px] font-bold uppercase tracking-[0.06em] text-muted-foreground"
              title={
                lineItem.seedQuantity != null
                  ? `Seeded qty ${lineItem.seedQuantity}${
                      lineItem.seedDescription
                        ? ` · ${lineItem.seedDescription}`
                        : ''
                    }`
                  : 'Changed from machine seed'
              }
              data-testid={`seed-delta-${lineItem.id}`}
            >
              Edited
            </span>
          )}
          {lineItem.isAllowance && (
            <span className="shrink-0 rounded bg-warning/15 px-1.5 py-0.5 font-public-sans text-[10px] font-bold uppercase tracking-[0.06em] text-warning">
              Allowance
            </span>
          )}
        </div>

        <span
          className="whitespace-nowrap font-mono text-foreground-soft sm:justify-self-end sm:text-right"
          data-col="quantity"
        >
          {editable ? (
            <InlineTextField
              value={lineItem.quantity ?? ''}
              ariaLabel="Line item quantity"
              className="w-10 text-right font-mono"
              onCommit={async (next) => {
                const trimmed = next.trim();
                if (trimmed === lineItem.quantity) return;
                if (!isNumericString(trimmed)) return;
                await onLineItemPatch!(lineItem.id, { quantity: trimmed });
              }}
            />
          ) : (
            <span>{lineItem.quantity}</span>
          )}
        </span>

        <span
          className="truncate text-xs text-muted-foreground sm:justify-self-start"
          data-col="unit"
          title={lineItem.customUnit ?? lineItem.unit}
        >
          {lineItem.customUnit ?? lineItem.unit}
        </span>

        <span
          className="whitespace-nowrap font-mono text-sm font-semibold text-foreground sm:justify-self-end sm:text-right"
          data-col="line-total"
        >
          {formatMoney(lineItem.lineTotal)}
        </span>

        <div className="flex justify-end sm:justify-self-end">
          {deletable ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={deleting}
              aria-label="Delete line item"
              title="Delete line item"
              className="h-auto px-1 py-0 text-xs font-semibold text-destructive hover:text-destructive"
              onClick={() => {
                void handleDelete();
              }}
            >
              Delete
            </Button>
          ) : (
            <span className="hidden sm:inline" aria-hidden />
          )}
        </div>
      </div>

      {unresolvedReason && (
        <p
          className="mt-0.5 font-public-sans text-xs text-muted-foreground"
          data-testid="unresolved-reason"
        >
          {unresolvedReason}
        </p>
      )}

      {/* Nested unit-cost rows — Option B: no delete on the child. */}
      {caps.showCostBreakdown &&
        (lineItem.costs ?? []).map((c) => (
          <CostRowDisplay
            key={c.id}
            cost={c}
            dedupeDescription={rawDesc}
            showHistoryLink={caps.showCostHistoryLink}
            onCostRow={costEditable ? onCostRow : undefined}
            onShowHistory={
              onShowCostHistory != null
                ? () => onShowCostHistory(lineItem.id, c.id)
                : undefined
            }
          />
        ))}
      {caps.showCostBreakdown && costEditable && (
        <div className="mt-1 sm:pl-[calc(5.5rem+0.5rem)]">
          {addingCost ? (
            <AddCostInline
              onCancel={() => setAddingCost(false)}
              onCommit={async (payload) => {
                await onCostRow!.onAdd(lineItem.id, payload);
                setAddingCost(false);
              }}
            />
          ) : (
            <Button type="button" variant="ghost" size="sm"
              className="h-auto px-1 py-0.5 text-xs font-semibold text-primary hover:text-primary-dim"
              onClick={() => setAddingCost(true)}
            >
              + Add cost
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * "+ Add material · + Add service" pair. With onAddRequest (Option 4c)
 * the pair routes into the page drawer's MPT search; otherwise it
 * opens the legacy inline EST-L8 row.
 */
function AddSkuControls({
  elementId,
  fieldId,
  onLineItemCreate,
  onAddRequest,
  allowOther = false,
}: {
  elementId: string;
  fieldId: string | null;
  onLineItemCreate: LineItemCreateHandler;
  onAddRequest?: LineItemAddRequestHandler;
  allowOther?: boolean;
}) {
  const [adding, setAdding] = useState<'MATERIAL' | 'SERVICE' | 'OTHER' | null>(null);

  if (adding === 'OTHER') {
    return (
      <AddOtherInline
        onCancel={() => setAdding(null)}
        onCommit={async (payload) => {
          await onLineItemCreate(elementId, {
            type: 'OTHER',
            projectScopeElementFieldId: fieldId,
            description: payload.description,
            quantity: payload.quantity,
            unit: 'EA',
            purpose: payload.purpose,
            initialCost: {
              productMsku: null,
              serviceId: null,
              description: payload.description,
              unitCostSnapshot: payload.unitCost,
            },
          });
          setAdding(null);
        }}
      />
    );
  }

  if (onAddRequest != null) {
    return (
      <div className="flex flex-wrap items-center gap-1 py-1">
        <Button type="button" variant="ghost" size="sm"
          className="h-auto px-1 py-0.5 text-xs font-semibold text-primary hover:text-primary-dim"
          onClick={() => onAddRequest({ elementId, fieldId, type: 'MATERIAL' })}
        >
          + Add material
        </Button>
        <span className="text-xs text-muted-foreground" aria-hidden="true">·</span>
        <Button type="button" variant="ghost" size="sm"
          className="h-auto px-1 py-0.5 text-xs font-semibold text-primary hover:text-primary-dim"
          onClick={() => onAddRequest({ elementId, fieldId, type: 'SERVICE' })}
        >
          + Add service
        </Button>
        {allowOther && (
          <>
            <span className="text-xs text-muted-foreground" aria-hidden="true">·</span>
            <Button type="button" variant="ghost" size="sm"
              className="h-auto px-1 py-0.5 text-xs font-semibold text-primary hover:text-primary-dim"
              onClick={() => setAdding('OTHER')}
            >
              + Add Other
            </Button>
          </>
        )}
      </div>
    );
  }

  if (adding) {
    return (
      <AddSkuInline
        type={adding}
        onCancel={() => setAdding(null)}
        onCommit={async (payload) => {
          const sku = payload.sku.trim() || null;
          await onLineItemCreate(elementId, {
            type: adding,
            projectScopeElementFieldId: fieldId,
            description: payload.description,
            quantity: payload.quantity,
            unit: 'EA',
            purpose: payload.purpose,
            initialCost: {
              productMsku: adding === 'MATERIAL' ? sku : null,
              serviceId: adding === 'SERVICE' ? sku : null,
              description: payload.description,
              unitCostSnapshot: payload.unitCost,
            },
          });
          setAdding(null);
        }}
      />
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-1 py-1">
      <Button type="button" variant="ghost" size="sm"
        className="h-auto px-1 py-0.5 text-xs font-semibold text-primary hover:text-primary-dim"
        onClick={() => setAdding('MATERIAL')}
      >
        + Add material
      </Button>
      <span className="text-xs text-muted-foreground" aria-hidden="true">·</span>
      <Button type="button" variant="ghost" size="sm"
        className="h-auto px-1 py-0.5 text-xs font-semibold text-primary hover:text-primary-dim"
        onClick={() => setAdding('SERVICE')}
      >
        + Add service
      </Button>
      {allowOther && (
        <>
          <span className="text-xs text-muted-foreground" aria-hidden="true">·</span>
          <Button type="button" variant="ghost" size="sm"
            className="h-auto px-1 py-0.5 text-xs font-semibold text-primary hover:text-primary-dim"
            onClick={() => setAdding('OTHER')}
          >
            + Add Other
          </Button>
        </>
      )}
    </div>
  );
}


function TypeChip({ type }: { type?: 'MATERIAL' | 'SERVICE' | 'OTHER' }) {
  if (!type) return null;
  const label = type === 'SERVICE' ? 'Svc' : type === 'OTHER' ? 'Other' : 'Prod';
  const tone =
    type === 'SERVICE'
      ? 'bg-success/15 text-success'
      : type === 'OTHER'
        ? 'bg-muted text-foreground-soft'
        : 'bg-primary/10 text-primary';
  return (
    <span
      className={`shrink-0 rounded px-1.5 py-0.5 font-public-sans text-[10px] font-bold uppercase tracking-[0.06em] ${tone}`}
    >
      {label}
    </span>
  );
}

/** First catalog ref on the line item's cost rows, if any. */
function skuCodeFor(lineItem: LineItem): string | null {
  for (const c of lineItem.costs ?? []) {
    if (c.productMsku) return c.productMsku;
    if (c.serviceId) return c.serviceId;
  }
  return null;
}

/**
 * Click-to-edit text field. Renders the value as plain text until
 * clicked; then becomes an `<input>` that commits on blur or Enter and
 * cancels on Escape. Trims whitespace before commit.
 */
function InlineTextField({
  value,
  ariaLabel,
  className = '',
  truncateDisplay = false,
  onCommit,
}: {
  value: string;
  ariaLabel: string;
  className?: string;
  /** Polish #6 — single-line ellipsis display (line-item titles). */
  truncateDisplay?: boolean;
  onCommit: (next: string) => void | Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [busy, setBusy] = useState(false);

  if (!editing) {
    return (
      <Button type="button" variant="ghost"
        className={`h-auto min-w-0 cursor-text justify-start rounded px-1 py-0 text-left text-sm font-normal text-foreground hover:bg-surface-low hover:text-foreground ${
          truncateDisplay ? 'max-w-full flex-1' : 'whitespace-normal'
        } ${className}`}
        title={truncateDisplay ? value : undefined}
        onClick={() => {
          setDraft(value);
          setEditing(true);
        }}
        aria-label={`Edit ${ariaLabel}`}
      >
        {truncateDisplay ? (
          /* Micro #1 — mobile: one line + ellipsis; sm+: wrap to 2 lines. */
          <span className="max-w-full whitespace-normal text-left line-clamp-1 sm:line-clamp-2">
            {value || <span className="italic text-muted-foreground">—</span>}
          </span>
        ) : (
          value || <span className="italic text-muted-foreground">—</span>
        )}
      </Button>
    );
  }

  const commit = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await onCommit(draft);
    } finally {
      setBusy(false);
      setEditing(false);
    }
  };

  return (
    <Input
      autoFocus
      disabled={busy}
      value={draft}
      aria-label={ariaLabel}
      className={`h-7 rounded px-1 ${className}`}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => void commit()}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          void commit();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          setDraft(value);
          setEditing(false);
        }
      }}
    />
  );
}

/** "railing sections" → "Railing sections" (UNRESOLVED title casing). */
function capitalizeFirst(s: string): string {
  return s.length > 0 ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

function isNumericString(s: string): boolean {
  if (!s) return false;
  const n = Number(s);
  return Number.isFinite(n) && n >= 0;
}

function toNumber(s: string | undefined | null): number {
  if (s == null || s === '') return 0;
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
}

function sumLineTotals(items: LineItem[]): number {
  return items.reduce((sum, li) => sum + toNumber(li.lineTotal), 0);
}

/** "$ 30,500" for whole amounts; cents kept when present ("$ 522.40"). */
function formatMoney(s: string | undefined | null): string {
  if (s == null || s === '') return '—';
  const n = Number(s);
  if (!Number.isFinite(n)) return s;
  const hasCents = Math.abs(n % 1) > 0.004;
  return `$ ${n.toLocaleString('en-US', {
    minimumFractionDigits: hasCents ? 2 : 0,
    maximumFractionDigits: 2,
  })}`;
}

/** Margin percentage without trailing zeros ("15", "12.5"). */
function trimPct(n: number): string {
  return String(Number(n.toFixed(2)));
}

/**
 * Sprint 41 W26 — Section III source-layer tooltip.
 */
function SourceLayerPill({
  costSource,
  snapshot,
}: {
  costSource: string;
  snapshot: NonNullable<CostRow['sourceLayerSnapshot']> | null;
}) {
  const hasSnapshot = snapshot != null && Object.keys(snapshot).length > 0;
  if (!hasSnapshot) {
    return (
      <span className="rounded bg-surface-low px-1.5 py-0.5 text-[10px] font-medium text-foreground-soft">
        {costSource}
      </span>
    );
  }

  const layers: Array<{ key: string; label: string; value?: number }> = [
    { key: 'INVOICE', label: 'Invoice (CachedCost)', value: snapshot.invoice },
    { key: 'PARTNER', label: 'Partner (SupplierSkuMapping)', value: snapshot.partner },
    { key: 'DEFAULT', label: 'Default (catalog)', value: snapshot.default },
    { key: 'LABOR', label: 'Labor (LaborCategory)', value: snapshot.labor },
  ];

  return (
    <span className="group relative">
      <Button type="button" variant="ghost"
        aria-label={`Cost source: ${costSource}. Hover or focus to see all resolver layers.`}
        className="h-auto cursor-help rounded bg-surface-low px-1.5 py-0.5 text-[10px] font-medium text-foreground-soft hover:bg-surface hover:text-foreground-soft">
        {costSource}
      </Button>
      <div
        role="tooltip"
        className="pointer-events-none invisible absolute right-0 z-10 mt-1 w-64 -translate-y-1 rounded border border-outline-variant bg-card p-2 text-[11px] shadow-lg opacity-0 transition group-hover:visible group-hover:translate-y-0 group-hover:opacity-100 group-focus-within:visible group-focus-within:translate-y-0 group-focus-within:opacity-100"
      >
        <p className="mb-1 font-semibold text-foreground-soft">Resolver cascade</p>
        <ul className="space-y-0.5">
          {layers.map((layer) => {
            const present = typeof layer.value === 'number';
            const chosen = layer.key === costSource;
            return (
              <li
                key={layer.key}
                className={
                  'flex items-baseline justify-between gap-2 rounded px-1 py-0.5 ' +
                  (chosen
                    ? 'bg-primary-tint font-semibold text-primary'
                    : present
                      ? 'text-foreground-soft'
                      : 'text-muted-foreground')
                }
              >
                <span>{layer.label}</span>
                <span className="font-mono">
                  {present ? formatLayerValue(layer.value!) : '—'}
                </span>
              </li>
            );
          })}
        </ul>
        {Object.keys(snapshot).length === 0 && (
          <p className="mt-1 italic text-muted-foreground">No layers reported.</p>
        )}
      </div>
    </span>
  );
}

function formatLayerValue(n: number): string {
  const fixed = n.toFixed(4);
  const trimmed = fixed.replace(/0+$/, '').replace(/\.$/, '');
  return trimmed.includes('.') ? trimmed : `${trimmed}.00`;
}

function CostRowDisplay({
  cost,
  dedupeDescription,
  showHistoryLink,
  onCostRow,
  onShowHistory,
}: {
  cost: CostRow;
  /**
   * Polish round 2026-07-23 #3 — the parent line item's description.
   * Derivation cost rows copy it verbatim; repeating it under the row
   * doubled the text, so an identical description renders as a plain
   * "Unit cost" label instead.
   */
  dedupeDescription?: string;
  showHistoryLink: boolean;
  onCostRow?: CostRowHandlers;
  onShowHistory?: () => void;
}) {
  const effective = cost.unitCostOverride ?? cost.unitCostSnapshot;
  const editable = onCostRow != null;
  const label =
    dedupeDescription != null && cost.description === dedupeDescription
      ? 'Unit cost'
      : cost.description;
  return (
    // Option B — nest under parent on the same column grid. Amount sits
    // under Total; no delete × on the child (Delete lives on the parent).
    <div
      className={`mt-1 text-xs text-foreground-soft ${LINE_ITEM_GRID}`}
      data-cost-id={cost.id}
      data-testid="cost-row"
    >
      <span className="hidden sm:block" aria-hidden />
      <span className="hidden sm:block" aria-hidden />
      <div className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-1 pl-1 sm:pl-0">
        <span className="min-w-0 max-w-full truncate" title={cost.description}>
          {label}
        </span>
        {cost.unitCostOverride && (
          <Button type="button" variant="ghost"
            disabled={!editable}
            title={editable ? 'Click to clear the override' : undefined}
            className={
              'h-auto shrink-0 rounded bg-warning-tint px-1.5 py-0.5 text-[10px] font-semibold text-warning' +
              (editable ? ' hover:bg-warning-tint hover:text-warning' : '')
            }
            onClick={editable ? () => onCostRow!.onUnitCostOverride(cost.id, null) : undefined}
          >
            override
          </Button>
        )}
        <SourceLayerPill
          costSource={cost.costSource}
          snapshot={cost.sourceLayerSnapshot ?? null}
        />
      </div>
      <span className="hidden sm:block" aria-hidden />
      <span className="hidden sm:block" aria-hidden />
      <div
        className="flex items-baseline justify-end gap-1 sm:justify-self-end"
        data-col="unit-cost-amount"
      >
        {editable ? (
          <InlineTextField
            value={effective ?? ''}
            ariaLabel="Cost unit price"
            className="w-14 text-right font-mono text-xs"
            onCommit={async (next) => {
              const trimmed = next.trim();
              const current = cost.unitCostOverride ?? cost.unitCostSnapshot ?? '';
              if (trimmed === current) return;
              if (!isNumericString(trimmed)) return;
              await onCostRow!.onUnitCostOverride(cost.id, trimmed);
            }}
          />
        ) : (
          <span className="font-mono">{effective}</span>
        )}
      </div>
      <div className="flex justify-end sm:justify-self-end">
        {showHistoryLink && onShowHistory != null ? (
          <Button type="button" variant="ghost" size="sm"
            aria-label="Show cost history"
            title="Show cost history"
            className="h-auto shrink-0 px-1 py-0 text-xs font-semibold text-primary hover:text-primary-dim"
            onClick={onShowHistory}>
            History
          </Button>
        ) : (
          <span className="hidden sm:inline" aria-hidden />
        )}
      </div>
    </div>
  );
}

/**
 * Sprint 41 W25 — inline form rendered under a line item when the
 * estimator clicks "+ Add cost". Two required inputs (description +
 * unitCostSnapshot).
 */
function AddCostInline({
  onCommit,
  onCancel,
}: {
  onCommit: (payload: {
    description: string;
    unitCostSnapshot: string;
  }) => Promise<void>;
  onCancel: () => void;
}) {
  const [description, setDescription] = useState('');
  const [unitCost, setUnitCost] = useState('');
  const [busy, setBusy] = useState(false);
  const valid = description.trim().length > 0 && isNumericString(unitCost.trim());

  const submit = async () => {
    if (!valid || busy) return;
    setBusy(true);
    try {
      await onCommit({
        description: description.trim(),
        unitCostSnapshot: unitCost.trim(),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      className="flex flex-wrap items-center gap-2 rounded border border-outline-variant bg-surface-low px-2 py-1.5 text-xs"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <Input
        autoFocus
        disabled={busy}
        value={description}
        placeholder="Description"
        aria-label="New cost description"
        className="h-7 flex-1 rounded border border-outline-variant px-1.5 py-0.5"
        onChange={(e) => setDescription(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onCancel();
        }}
      />
      <Input
        disabled={busy}
        value={unitCost}
        placeholder="0.00"
        aria-label="New cost unit price"
        className="h-7 w-24 rounded border border-outline-variant px-1.5 py-0.5 text-right font-mono"
        onChange={(e) => setUnitCost(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onCancel();
        }}
      />
      <Button type="submit" size="sm"
        disabled={!valid || busy}
        className="h-7 rounded px-2 py-0.5 text-xs font-semibold">
        Save
      </Button>
      <Button type="button" variant="ghost" size="sm"
        disabled={busy}
        onClick={onCancel}
        className="h-7 rounded px-2 py-0.5 text-xs text-foreground-soft hover:bg-surface hover:text-foreground">
        Cancel
      </Button>
    </form>
  );
}

function TotalRow({
  label,
  value,
  bold,
}: {
  label: string;
  value?: string;
  bold?: boolean;
}) {
  if (value == null) return null;
  return (
    // Micro #3 — explicit gap so label and amount never touch when the
    // row squeezes at mobile widths.
    <div className="flex items-baseline justify-between gap-3 py-1">
      <span
        className={`font-public-sans text-sm ${
          bold ? 'font-bold text-foreground' : 'text-foreground-soft'
        }`}
      >
        {label}
      </span>
      {/* Polish #7 — the amount never splits across lines. */}
      <span
        className={`whitespace-nowrap font-mono text-sm ${
          bold ? 'font-bold text-foreground' : 'text-foreground-soft'
        }`}
      >
        {value}
      </span>
    </div>
  );
}

/**
 * Fallback for frozen snapshots predating the statement engine: build
 * `Label: value` lines from the visible, contract-visible, answered
 * fields (member mirrors excluded via contractVisibility=HIDDEN).
 */
function fallbackStatementsFromFields(fields: ScopeField[]): ScopeStatement[] {
  return fields
    .filter(
      (f) =>
        f.visibilityResolved !== false &&
        f.contractVisibility !== 'HIDDEN' &&
        hasAnswer(f),
    )
    .map((f) => ({
      scfCode: f.scfCode,
      groupLabel: effectiveScopeGroupLabel(f.scfCode, f.groupLabel),
      text: `${f.scfLabel}: ${displayAnswer(f)}`,
      source: 'LABEL_VALUE',
    }));
}

/** EST-L4 — a row renders only for a selection actually made. */
function hasAnswer(field: ScopeField): boolean {
  if (field.answerDisplay != null && field.answerDisplay !== '') return true;
  const v = field.answerValue;
  if (v == null || v === '') return false;
  if (Array.isArray(v)) return v.length > 0;
  return true;
}

/**
 * Format-fix batch 2026-07 — read-only CSF value display. Prefers the
 * server-computed `answerDisplay`; the local fallback covers pre-batch
 * snapshots and mirrors the same rules; it never dumps raw JSON.
 */
function displayAnswer(field: ScopeField): string {
  if (field.answerDisplay != null && field.answerDisplay !== '') {
    return field.answerDisplay;
  }
  const value = field.answerValue;
  if (value == null || value === '') return '—';
  const optionLabel = (v: unknown): string => {
    if (typeof v === 'string') {
      const hit = field.options?.find((o) => o.value === v);
      if (hit) return hit.label;
    }
    if (typeof v === 'boolean') return v ? 'Yes' : 'No';
    return String(v);
  };
  if (field.inputType === 'BOOLEAN' || typeof value === 'boolean') {
    return value === true ? 'Yes' : 'No';
  }
  if (Array.isArray(value)) {
    return value
      .map((row, i) => {
        if (row != null && typeof row === 'object' && !Array.isArray(row)) {
          const parts = Object.entries(row as Record<string, unknown>)
            .filter(([, v]) => v != null && v !== '')
            .map(([k, v]) => `${k}: ${optionLabel(v)}`);
          return `#${i + 1} — ${parts.length > 0 ? parts.join(', ') : 'empty'}`;
        }
        return optionLabel(row);
      })
      .join('; ');
  }
  if (field.inputType === 'NUMBER' && field.unit) {
    return `${String(value)} ${field.unit}`;
  }
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    if ('value' in obj) {
      return displayAnswer({ ...field, answerValue: obj.value, answerDisplay: null });
    }
    return '—';
  }
  return optionLabel(value);
}

function VariationFamilyPickers({
  elements,
  onPick,
}: {
  elements: ScopeElement[];
  onPick: (id: string) => void;
}) {
  const seen = new Set<string>();
  const families: ScopeElement[][] = [];
  for (const element of elements) {
    const family = variationFamilyMembers(elements, element);
    if (family.length < 2) continue;
    const root = family.find((row) => !row.parentVariationOfId)?.id ?? family[0].id;
    if (seen.has(root)) continue;
    seen.add(root);
    families.push(family);
  }
  if (families.length === 0) return null;
  return (
    <div className="space-y-3 px-6 pb-4" data-testid="intake-variation-contract-pickers">
      {families.map((family) => (
        <fieldset key={family[0].id} className="rounded-md border border-border p-3">
          <legend className="px-1 font-public-sans text-xs font-bold uppercase tracking-wider text-muted-foreground">
            Choose one
          </legend>
          <RadioGroup
            value={
              family.find((row) => row.clientIncluded !== false)?.id ??
              family[0].id
            }
            onValueChange={onPick}
            className="space-y-2"
          >
            {family.map((member) => (
              <label
                key={member.id}
                className="flex items-center gap-2 font-public-sans text-sm"
              >
                <RadioGroupItem value={member.id} />
                <span>
                  {member.variationLabel ||
                    member.instanceLabel ||
                    member.elementTypeDisplayName ||
                    'Option'}
                </span>
              </label>
            ))}
          </RadioGroup>
        </fieldset>
      ))}
    </div>
  );
}
